import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Grid} from '@material-ui/core';
import {useDispatch, useSelector} from 'react-redux';
import {ReduxState} from '../../store/types';
import {actions} from '../../store';
import {useTranslation} from 'react-i18next';
import {convertUserLocalTimeToUtc} from '../../utils/dateWithTimezoneConversion';
import {CallHistory} from '../../store/types/CallHistory';
import CustomKeyboardDateTimePicker from '../../components/KeyboardDateTimePicker/KeyboardDateTimePicker';
import TextField from '../../components/TextField/TextField';
import Button from '../../components/Button/Button';
import {useFormik} from 'formik';
import dayjs from '../../services/customDayJs';
import CustomizedDialog from '../../components/Dialog/Dialog';
import CallRemoveBottomBox from '../../components/Calls/CallRemoveBottomBox';
import {prepareCallRecordName} from '../../utils/calls/prepareCallRecordName';
import DataGrid from '../../components/DataGrid/DataGrid';
import {PaginationMode} from '../../components/DataGrid/types';
import Header from '../../components/ListViewHeader/Header';
import {
    CallRecordingsFormProps,
    dateFormat,
    generateColumns,
    useStyles as useCallRecordingsStyles
} from '../CallRecordings/utils';
import toast from 'react-hot-toast';
import classNames from 'classnames';
import {dateFromDateTillValidationSchema} from '../../components/Extensions/CallHistoryDialog.utils';
import AudioPlayer from '../../components/AudioPlayer/AudioPlayer';
import {PlayingFile, RecordingsPlayingState, RecordingsPlayingStatus} from '../../store/reducers/calls/reducer';
import {cdrPlaybackKey, playbackIdentityOf} from '../../utils/playbackIdentity';
import {getPermission} from '../../utils/permissions/getPermission';
import {useRawPermissions} from '../../hooks/usePermissions';
import {Permission, PermissionType} from '../../store/types/Permission';
import CallRecordInformationTabsDialog
    from '../../components/Calls/CallRecordInformationTabsDialog/CallRecordInformationTabsDialog';
import AccountControlPanel from '../AccountControlPanel/AccountControlPanel';
import {useStyles} from './AccountCallRecordings.utils';

const AccountCallRecordings = () => {
    const classes = useStyles();
    const crClasses = useCallRecordingsStyles();
    const {t} = useTranslation();

    const [itemsToRemove, setItemsToRemove] = useState<CallHistory[]>([]);
    const [selectedItems, setSelectedItems] = useState<CallHistory[]>([]);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isDownloadingGoingOn, setIsDownloadingGoingOn] = useState<boolean[]>([]);
    const [isRemoving, setIsRemoving] = useState(false);

    const dispatch = useDispatch();

    const permissions = useRawPermissions();

    const [callRecordInformation, setCallRecordInformation] = useState<
        CallHistory | undefined
    >(undefined);
    const [,
        setCallRecordInformationInList,
    ] = useState<CallHistory | undefined>(undefined);

    const i_account = useSelector<ReduxState, number | undefined>(
        (state) => state.generic.sessionData?.i_account,
    );

    const {items, total, isLoading} = useSelector<
        ReduxState,
        {
            items: CallHistory[];
            total: number;
            isLoading: boolean;
        }
    >((state) => state.calls.extensionCallHistory);

    const customerCurrency = useSelector<ReduxState, string>(
        (state) => state.calls.customerCurrency || '',
    );

    const timezoneOffset = useSelector<ReduxState, number>(
        (state) => state.generic.sessionData?.tz_offset || 0
    );

    const userDateTimeFormat = useSelector<ReduxState, string>(
        (state) =>
            state.accountControlPanel?.accountDetails?.out_date_time_format
            || state.generic.globalCustomerInfo?.customer_info?.out_date_time_format
            || state.generic.sessionData?.out_date_time_format
            || '',
    );

    const recordingsPlayer: RecordingsPlayingState = useSelector<ReduxState, RecordingsPlayingState>(
        (state) => state.calls.recordingsPlayer,
    );

    const audioFilePlaying = useMemo(() => {
        return !(!recordingsPlayer || recordingsPlayer.status === RecordingsPlayingStatus.notDefined
            || recordingsPlayer.status === RecordingsPlayingStatus.dowloadingRestricted);
    }, [recordingsPlayer]);

    const initialValues = useMemo(
        () => ({
            from: dayjs
                .utc()
                .utcOffset(timezoneOffset / 60)
                .subtract(1, 'month')
                .format(dateFormat),
            till: dayjs
                .utc()
                .utcOffset(timezoneOffset / 60)
                .format(dateFormat),
            cli: '',
            cld: '',
        }),
        [timezoneOffset],
    );

    const {
        values,
        handleSubmit,
        handleChange,
        setFieldValue,
        errors,
        setFieldError,
    } = useFormik<CallRecordingsFormProps>({
        initialValues,
        onSubmit: (form) => {
            getCallHistory(form, false);
        },
        validationSchema: dateFromDateTillValidationSchema(timezoneOffset, userDateTimeFormat),
        enableReinitialize: true,
        validateOnChange: false,
    });

    const getCallHistory = (
        formData: CallRecordingsFormProps,
        withInitCalls?: boolean,
        limit?: number,
        offset?: number,
    ) => {

        dispatch(
            actions.getExtensionCallHistory.request({
                i_account: i_account || 0,
                limit: limit || 10,
                offset: offset || 0,
                from: convertUserLocalTimeToUtc(formData.from, timezoneOffset),
                till: convertUserLocalTimeToUtc(formData.till, timezoneOffset),
                cli: formData.cli ? `${formData.cli}%` : undefined,
                cld: formData.cld ? `${formData.cld}%` : undefined,
                withInitCalls,
                isCallRecording: true,
            }),
        );
    };

    useEffect(() => {
        getCallHistory(values, true);
    }, []);


    const getDataExtensionRequestPayload = () => {
        return {
            i_account: i_account || 0,
            limit: 10,
            offset: 0,
            from: convertUserLocalTimeToUtc(values.from, timezoneOffset),
            till: convertUserLocalTimeToUtc(values.till, timezoneOffset),
            cli: values.cli ? `${values.cli}%` : undefined,
            cld: values.cld ? `${values.cld}%` : undefined,
            isCallRecording: true,
        };
    };

    const toogleTooltipVisibility = (index: number) => {
        const newDownloadings = [...isDownloadingGoingOn];
        newDownloadings[index] = false;
        setIsDownloadingGoingOn(newDownloadings);
    };

    const downloadFile = (
        i_xdr: number,
        call_recording_id: string,
        fileName: string,
        index: number,
    ) => {
        const newDownloading = [...isDownloadingGoingOn];
        newDownloading[index] = true;
        setIsDownloadingGoingOn(newDownloading);
        toast(t<string>('tooltips:ringGroups.downloadWillStartShortly'));
        dispatch(
            actions.getExtensionCallHistoryFile.request({
                i_xdr,
                call_recording_id,
                fileName,
                callback: () => toogleTooltipVisibility(index),
            }),
        );
    };

    const removeItems = (items: CallHistory[]) => {
        setIsRemoving(true);
        dispatch(
            actions.removeCallRecordings.request({
                items,
                getDataExtensionRequestPayload: getDataExtensionRequestPayload(),
            }),
        );
        setItemsToRemove([]);
        setSelectedItems([]);
        setCallRecordInformation(undefined);
    };

    useEffect(() => {
        if (isRemoving && !isLoading) {
            setIsRemoving(false);
        }
    }, [isLoading]);

    const toggleCallRecordInformationInList = (item: CallHistory) =>
        setCallRecordInformationInList(item);

    const allowDelete = getPermission(permissions, ...Permission.Calls.Recordings.DeleteCallRecording.value)
        === PermissionType.Visible;

    const columns = useMemo(
        () =>
            generateColumns(
                t,
                crClasses,
                timezoneOffset,
                downloadFile,
                setCallRecordInformation,
                (items) => {
                    setItemsToRemove(items);
                },
                userDateTimeFormat,
                isDownloadingGoingOn,
                !!callRecordInformation,
                toggleCallRecordInformationInList,
                recordingsPlayer.filesQueue
            ),
        [t, timezoneOffset, userDateTimeFormat, isDownloadingGoingOn, recordingsPlayer.filesQueue],
    );

    const onChangeItemsSelection = useCallback(
        (items) => setSelectedItems(items as CallHistory[]),
        [],
    );

    const handleSubmitRemove = () => {
        removeItems(itemsToRemove);
    };

    useEffect(() => {
        callRecordInformation && setIsDetailsModalOpen(true);
    }, [callRecordInformation]);

    useEffect(() => {
        dispatch(actions.updateRecordingsPlayingState({
            status: RecordingsPlayingStatus.notDefined,
            currentFile: null,
            currentBlob: null,
            filesQueue: items.map(e => {
                const downloadIds = e.cr_download_ids || [];
                const i_xdr = e.i_xdr;
                if (!downloadIds.length || !i_xdr) return [];
                return downloadIds.map(c => {
                    return {
                        call_recording_id: c,
                        i_xdr: i_xdr,
                        playbackKey: cdrPlaybackKey(c, i_xdr)
                    } as PlayingFile;
                });
            })
        }));
    }, [items]);

    return (
        <div className={classes.mainDashboardContainer}>
            <div className={classes.header}>
                <Header
                    title={t('screens:calls.callRecordings')}
                    totalNumber={total || 0}
                    dataQa={'device-list-view-header'}
                    showDetails={false}
                    customClass={classes.listViewHeader}
                />
                <div className={classes.rightSection}>
                    <AccountControlPanel />
                </div>
            </div>
            <div className={classNames(crClasses.scrollable, classes.containerBg)}>
                    <form
                        onSubmit={handleSubmit}
                        autoComplete="off"
                        data-testid="call-history-form"
                    >
                        <Grid item className={classNames(crClasses.filtersContainer, classes.filtersContainer)}>
                            <Grid item className={classNames(crClasses.itemsContainer,classes.itemsContainer)}>
                                <Grid item className={crClasses.inputsContainer}>
                                    <CustomKeyboardDateTimePicker
                                        id="from"
                                        label={t('common:fromDate')}
                                        value={values.from}
                                        onChange={(v) =>
                                            setFieldValue('from', v)
                                        }
                                        dataQa={'call-history-filter-from'}
                                        dataTestId={'call-history-filter-from'}
                                        userDateTimeFormat={userDateTimeFormat}
                                        handleSubmit={handleSubmit}
                                        helperText={errors.from}
                                        setFieldError={setFieldError}
                                        skipPermission
                                    />
                                    <CustomKeyboardDateTimePicker
                                        id="till"
                                        label={t('common:toDate')}
                                        value={values.till}
                                        onChange={(v) =>
                                            setFieldValue('till', v)
                                        }
                                        dataQa={'call-history-filter-till'}
                                        dataTestId={'call-history-filter-till'}
                                        userDateTimeFormat={userDateTimeFormat}
                                        handleSubmit={handleSubmit}
                                        helperText={errors.till}
                                        setFieldError={setFieldError}
                                        skipPermission
                                    />
                                    <TextField
                                        id="cli"
                                        label={t('screens:calls.caller')}
                                        onChange={handleChange}
                                        value={values.cli}
                                        dataQa={
                                            'call-history-filter-calling-number'
                                        }
                                        handleSubmit={handleSubmit}
                                        className={crClasses.cropLabelTextInFilter}
                                        skipPermission
                                    />
                                    <TextField
                                        id="cld"
                                        label={t('screens:calls.destination')}
                                        onChange={handleChange}
                                        value={values.cld}
                                        dataQa={
                                            'call-history-filter-called-number'
                                        }
                                        handleSubmit={handleSubmit}
                                        className={crClasses.cropLabelTextInFilter}
                                        skipPermission
                                    />
                                </Grid>
                                <Button
                                    primary
                                    accent
                                    dataQa="call-history-search"
                                    className={crClasses.button}
                                    onClick={() => handleSubmit()}
                                    skipPermission
                                >
                                    {t('common:search')}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>

                    <DataGrid<CallHistory>
                        columns={columns}
                        data={items}
                        rowCount={total}
                        loading={isLoading}
                        onPageChange={(v) =>
                            getCallHistory(
                                values,
                                false,
                                v.pageSize,
                                v.pageSize * v.page,
                            )
                        }
                        onPageSizeChange={(v) =>
                            getCallHistory(values, false, v.pageSize, 0)
                        }
                        paginationMode={PaginationMode.Server}
                        centeredRows
                        narrowRows
                        multiSelectEnabled={allowDelete}
                        onChangeItemsSelection={onChangeItemsSelection}
                        initialPageSize={10}
                        getItemId={(v) => v.i_xdr!}
                        classes={{
                            tableContainer: classNames(crClasses.table, classes.table),
                        }}
                        customRowStyle={(row) => {
                            const downloadIds = row.original.cr_download_ids || [];
                            const i_xdr = row.original.i_xdr;
                            if (downloadIds.length && (
                                recordingsPlayer.status === RecordingsPlayingStatus.playing ||
                                recordingsPlayer.status === RecordingsPlayingStatus.paused)) {
                                if (!(recordingsPlayer?.currentFile)) return '';
                                for (const f of downloadIds) {
                                    if (f && playbackIdentityOf(recordingsPlayer.currentFile) === cdrPlaybackKey(f, i_xdr))
                                        return crClasses.playingMusicRow;
                                }
                            }
                            return '';
                        }}
                    />

                    {audioFilePlaying && (
                        <AudioPlayer customClasses={{
                            rootContainer: classNames(crClasses.audioBox, classes.audioBox,
                                (selectedItems.length > 0) && crClasses.audioBoxAboveRemoveBox)
                        }}
                                     dataQa={'audio-player-element'}
                                     dataTestId={'audio-player-element'}
                                     visible={audioFilePlaying}
                                     downloadTranscriptionPermission={Permission.Calls.Recordings.PlayCallRecording.DownloadCallTranscription.value}
                        />
                    )}

                    <CallRecordInformationTabsDialog
                        isOpen={isDetailsModalOpen}
                        toggleVisibility={() => {
                            setIsDetailsModalOpen(prev => !prev);
                            setCallRecordInformation(undefined);
                        }}
                        callHistory={callRecordInformation}
                        filesQueue={recordingsPlayer.filesQueue}
                        customerCurrency={customerCurrency}
                        timezoneOffset={timezoneOffset}
                        onDownloadClick={() =>
                            callRecordInformation?.i_xdr &&
                            downloadFile(
                                callRecordInformation.i_xdr,
                                callRecordInformation.cr_download_ids?.[0] ||
                                '',
                                prepareCallRecordName(
                                    callRecordInformation,
                                    timezoneOffset,
                                ),
                                0,
                            )
                        }
                        onDeleteClick={() => {
                            callRecordInformation?.i_xdr &&
                            setItemsToRemove([callRecordInformation]);
                        }}
                        isDownloadingGoingOn={isDownloadingGoingOn}
                        userDateTimeFormat={userDateTimeFormat}
                        deletePermission={Permission.Calls.Recordings.CallDetailRecord.DeleteCallRecording.value}
                        playPermission={Permission.Calls.Recordings.CallDetailRecord.PlayCallRecording.value}
                        downloadPermission={Permission.Calls.Recordings.CallDetailRecord.DownloadCallRecording.value}
                        downloadTranscriptionPermission={Permission.Calls.Recordings.CallDetailRecord.DownloadCallTranscription.value}
                    />

                    <CustomizedDialog
                        isOpen={!!itemsToRemove.length || isRemoving}
                        handleClose={() => setItemsToRemove([])}
                        handleSubmit={handleSubmitRemove}
                        customClasses={{
                            container: classNames(
                                itemsToRemove.length === 1 &&
                                crClasses.containerDelete,
                            ),
                        }}
                        content={
                            !!itemsToRemove.length && (
                                <span className={crClasses.deleteConfirmation}>
                                {itemsToRemove.length === 1
                                    ? `${t(
                                        'screens:calls.deleteCallHistory',
                                        {
                                            name: prepareCallRecordName(
                                                itemsToRemove[0],
                                                timezoneOffset,
                                            ),
                                        },
                                    )}`
                                    : t(
                                        'screens:calls.deleteSelectedRecordsQuestion',
                                        {
                                            value: itemsToRemove.length,
                                        },
                                    )}
                            </span>
                            )
                        }
                        closeButtonText={t('common:cancel')}
                        submitButtonText={t('common:delete')}
                        dataQa="remove-call-records-dialog"
                        isLoading={isRemoving}
                        loadingMessage={t('screens:calls.deletingRecord')}
                    />

                    <CallRemoveBottomBox
                        numberOfItems={selectedItems.length}
                        onDelete={() => setItemsToRemove([...selectedItems])}
                        customClasses={{
                            buttonsContainer: crClasses.buttonsContainer,
                        }}
                    />
            </div>
        </div>
    );
};

export default AccountCallRecordings;
